<template>
  <div class="container profile">
    <div v-if="patient.initializing" class="row">
      <Loader style="width: 100%" />
    </div>

    <div v-else class="row">
      <div class="col-12 col-lg-4">
        <div class="dashboard-card profile-block">
          <div class="avatar">
            <div
              v-if="patient.photoUrl"
              class="user-avatar"
              role="img"
              aria-label="Uploaded Avatar Photo"
              :style="{ backgroundImage: `url(${patient.photoUrl})` }"
            />
            <div v-else class="user-avatar" />
            <h2>{{ patient.fullName }}</h2>
            <table class="table bm-table mt-4 mb-0">
              <tbody class="text-left">
                <tr>
                  <td class="col-7">Status:</td>
                  <td class="text-right">
                    <span v-if="patient.school.isFaculty"> Staff </span>
                    <span v-else-if="patient.isFullTimeStudent">
                      Full Time Student
                    </span>
                    <span v-else> Part Time Student </span>
                  </td>
                </tr>

                <tr>
                  <td>Individual Credits Remaining:</td>
                  <td class="text-right">
                    {{ patient.individualCreditsRemaining }}
                  </td>
                </tr>

                <tr v-if="!patient.school.isFaculty">
                  <td>Wellbeing Workshop Credits Remaining:</td>
                  <td class="text-right">
                    {{ patient.groupCreditsRemaining }}
                  </td>
                </tr>

                <tr
                  v-if="!patient.school.isFaculty && patient.isInternationalStudent"
                >
                  <td>International:</td>
                  <td class="text-right">Yes</td>
                </tr>
                <tr
                  v-if="!patient.school.isFaculty && patient.isStudentAthlete"
                >
                  <td>Athlete:</td>
                  <td class="text-right">Yes</td>
                </tr>
                <tr v-if="!patient.school.isFaculty && patient.studentIdNumber">
                  <td>Student Id:</td>
                  <td class="text-right">
                    {{ patient.studentIdNumber }}
                  </td>
                </tr>
                <tr v-if="!patient.school.isFaculty && patient.classYear">
                  <td>Class Year:</td>
                  <td class="text-right">
                    {{ patient.classYear }}
                  </td>
                </tr>
              </tbody>
            </table>
            <hr class="mt-0" />
            <ul class="info-list">
              <li>
                <button class="btn btn-primary btn-wide" @click="sendMessage()">
                  <font-awesome-icon
                    :icon="['fas', 'envelope']"
                    class="icon alt"
                  />
                  Send Message
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div class="dashboard-card">
          <session-history />
        </div>
      </div>

      <div class="col-12 col-lg-8">
        <div class="dashboard-card light">
          <div class="row">
            <div class="col-8">
              <h2>Bio/About</h2>
            </div>
          </div>
          <p v-if="!patient.bio">This user hasn't filled out their bio yet.</p>
          <p v-else>
            {{ patient.bio }}
          </p>

          <h3 style="margin-top: 40px; margin-bottom: 20px; font-size: 1.5rem">
            More Info
          </h3>

          <table class="table bm-table">
            <tbody>
              <tr v-if="patient.email">
                <td>Email:</td>
                <td class="text-right">
                  {{ patient.email }}
                </td>
              </tr>
              <tr v-if="patient.phoneNumber">
                <td>Phone:</td>
                <td class="text-right">
                  {{ patient.phoneNumber }}
                </td>
              </tr>
              <tr v-if="patient.gender">
                <td>Gender Identity:</td>
                <td class="text-right">
                  {{ getStudentGenderDisplayName(patient.gender) }}
                </td>
              </tr>
              <tr>
                <td>Ethnicity:</td>
                <td class="text-right">
                  {{
                    patient.ethnicity || 'Preferred not to answer' | capitalize
                  }}
                </td>
              </tr>
              <tr v-if="patient.city">
                <td>Current City:</td>
                <td class="text-right">
                  {{ patient.city }}, {{ patient.state }}
                </td>
              </tr>
              <tr v-if="patient.dateOfBirth">
                <td>Date of Birth:</td>
                <td class="text-right">
                  {{ patient.dateOfBirth | dateonly }}
                </td>
              </tr>
            </tbody>
          </table>

          <template v-if="patient.struggles.length">
            <b class="d-block mt-4"> I am Experiencing... </b>
            <ul class="pills">
              <li v-for="struggle in patient.struggles" :key="struggle.id">
                {{ struggle.name }}
              </li>
            </ul>
          </template>
        </div>
        <div class="dashboard-card light">
          <h2>{{ patient.school.name }} Contacts, Policies & Protocols</h2>

          <br />
          <a :href="patient.authReleaseFormUrl" target="about:blank">
            <font-awesome-icon
              :icon="['fas', 'file-download']"
              class="icon alt"
            />
            User Information Authorization for Release Form
          </a>
          <br />
          <br />

          <template
            v-if="
              patient.school.crisisContact && patient.school.crisisContact.name
            "
          >
            <h5>
              <font-awesome-icon
                :icon="['fas', 'exclamation-triangle']"
                class="icon alt"
              />
              In an emergency please reach out to the campus crisis contact:
            </h5>
            <p>{{ patient.school.crisisContact.name }}</p>
            <p>{{ patient.school.crisisContact.email }}</p>
            <p>{{ patient.school.crisisContact.phoneNumber }}</p>

            <hr />
          </template>

          <h5>
            <font-awesome-icon
              :icon="['fas', 'exclamation-triangle']"
              class="icon alt"
            />
            User Emergency Protocol
          </h5>
          <p>{{ patient.school.triage.crisis }}</p>

          <template v-if="patient.school.triage.nonCrisis">
            <hr />
            <h5>User Non-Emergency Protocol</h5>
            <p>{{ patient.school.triage.nonCrisis }}</p>
          </template>

          <template
            v-if="
              patient.school.counselingCenter.name ||
                patient.school.resources.onCampus
            "
          >
            <hr />
            <h5>On-Campus Resources available to users</h5>
          </template>
          <template v-if="patient.school.counselingCenter">
            <p>{{ patient.school.counselingCenter.name }}</p>
            <p>{{ patient.school.counselingCenter.location }}</p>
            <p>{{ patient.school.counselingCenter.phoneNumber }}</p>
          </template>

          <template v-if="patient.school.resources.onCampus">
            <br />
            <p>{{ patient.school.resources.onCampus }}</p>
          </template>

          <template v-if="patient.school.resources.local">
            <hr />
            <h5>Local Resources available to users</h5>
            <p>{{ patient.school.resources.local }}</p>
          </template>

          <template v-if="patient.school.triage.creditPolicy">
            <hr />
            <h5>Campus Credit Policy</h5>
            <p>{{ patient.school.triage.creditPolicy }}</p>
          </template>
        </div>
      </div>
    </div>
    <send-message-modal ref="sendMessageModal" />
  </div>
</template>

<script>
import Loader from '@/components/Loader.vue';
import SendMessageModal from '@/components/modals/SendMessageModal.vue';
import SessionHistory from './components/session-history.vue';
import PatientDetailModel from './patient-detail-model';
import genderOptions from '@/helpers/mixins/gender-options';

export default {
  name: 'PatientDetail',
  components: {
    Loader,
    SendMessageModal,
    SessionHistory
  },
  filters: {
    dateonly(value) {
      if (!value) return '';

      return value.format('DD/MM/YYYY');
    }
  },
  mixins: [genderOptions],
  provide() {
    return {
      patient: this.patient
    };
  },
  data() {
    return {
      patient: new PatientDetailModel(this.$http),
    };
  },
  async mounted() {
    try {
      const { id } = this.$route.params;
      let counselorId = 0;
      if (this.$store.getters.currentUser.counselor_profile) counselorId = this.$store.getters.currentUser.counselor_profile.id;
      await this.patient.initialize(counselorId, id);
    } catch (ex) {
      console.error(ex);
      this.$router.push({
        name: 'dashboard',
        params: { from: 'student-not-found' }
      });
    }
  },
  methods: {
    sendMessage() {
      this.$refs.sendMessageModal.show(this.patient.studentUserId);
    }
  }
};
</script>

<style lang="scss" scoped>
.profile {
  .table {
    td {
      vertical-align: middle;
    }
  }
  .avatar {
    text-align: center;

    .user-avatar {
      @include background-cover;
      @include border-radius(100px);

      background-image: url('~@/assets/img/default-user.jpg');
      height: 150px;
      width: 150px;
      margin-right: 15px;
      display: inline-block;
      vertical-align: top;
      margin-bottom: 15px;
    }

    p {
      opacity: 0.5;
    }
  }

  .info-list {
    list-style-type: none;
    padding-left: 0;
    font-size: 18px;
    margin-bottom: 0;

    li {
      margin-bottom: 5px;

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    a {
      color: white;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .pills {
    margin-bottom: 0;
    margin-top: 15px;
  }

  .insurance-block {
    label {
      @include label;

      margin-bottom: 15px;
    }

    p {
      font-size: 18px;
      margin-bottom: 0;
    }
  }

  .icon-btn {
    border: none;
    background: none;
    padding: 0;
    color: $green;
    font-size: 20px;
    margin-right: 20px;

    .fa-calendar-plus {
      font-size: 18px;
    }

    .fa-envelope,
    .fa-phone-square {
      font-size: 20px;
      position: relative;
      top: 2px;
    }
  }

  p {
    white-space: pre-wrap;
    margin: 0px;
  }
}
</style>
